import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
  INPUT_LENGTH_100,
  SWAL_SETTINGS,
  TEXTAREA_MAX_LENGTH_200,
} from "../../../../utils/Constants";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import {
  adminNotificationDetails,
  updateAdminNotification,
} from "../../services/notification.services";
import * as pujaServices from "../../services/puja.services";
import * as reportServices from "../../services/report.services";
import * as psychicServices from "../../services/psychic.services";
import * as productServices from "../../services/product.services";
import * as appMenuServices from "../../services/appMenu.services";

const EditNotification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [submitted, setSubmitted] = useState(false);

  const [pujas, setPujas] = useState([]);
  const [reports, setReports] = useState([]);
  const [astrologers, setAstrologers] = useState([]);
  const [products, setProducts] = useState([]);
  const [freeServices, setFreeServices] = useState([]);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: "Notifications", url: "/admin/notification" },
    { title: "Edit Notification", url: "" },
  ];

  useEffect(() => {
    // Fetch Pujas
    pujaServices.List({ page: 1, per_page: 500 })
      .then(res => setPujas(res?.data?.list || res?.data || []))
      .catch(err => console.error(err));

    // Fetch Reports
    reportServices.List({ page: 1, per_page: 500 })
      .then(res => setReports(res?.data?.list || res?.data || []))
      .catch(err => console.error(err));

    // Fetch Astrologers
    psychicServices.GetAllAstrologer()
      .then(res => setAstrologers(res?.data || []))
      .catch(err => console.error(err));

    // Fetch Products
    productServices.List({ page: 1, per_page: 500 })
      .then(res => setProducts(res?.data?.list || res?.data || []))
      .catch(err => console.error(err));

    // Fetch Free Services (App Menu Items)
    appMenuServices.ListItems({ page: 1, per_page: 500 })
      .then(res => setFreeServices(res?.data?.list || res?.data || []))
      .catch(err => console.error(err));
  }, []);

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .required("Title is required")
      .max(
        INPUT_LENGTH_100,
        `Title must be at most ${INPUT_LENGTH_100} characters`
      ),
    message: Yup.string()
      .trim()
      .required("Message is required")
      .max(
        TEXTAREA_MAX_LENGTH_200,
        `Message must be at most ${TEXTAREA_MAX_LENGTH_200} characters`
      ),
    redirect_type: Yup.string().nullable().optional(),
    redirect_id: Yup.string().nullable().optional(),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      message: "",
      redirect_type: "",
      redirect_id: "",
    },
    validationSchema,
    onSubmit: (values) => {
      globalLoader(true);
      setSubmitted(true);
      updateAdminNotification({
        id: Number(id),
        title: values.title.trim(),
        message: values.message.trim(),
        redirect_type: values.redirect_type || "",
        redirect_id: values.redirect_id || "",
      })
        .then((response) => {
          globalLoader(false);
          if (response?.success) {
            Swal.fire({
              icon: "success",
              text: response?.message || "Notification updated successfully.",
              ...SWAL_SETTINGS,
            });
            setTimeout(() => {
              navigate("/admin/notification");
            }, 1000);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            setSubmitted(false);
          }
        })
        .catch((error) => {
          console.log("updateNotificationError ====> ", error);
          globalLoader(false);
          setSubmitted(false);
          Swal.fire({
            icon: "error",
            text:
              error?.response?.data?.message ||
              "Something went wrong while updating notification.",
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  useEffect(() => {
    globalLoader(true);
    adminNotificationDetails(id)
      .then((response) => {
        if (response?.success) {
          formik.setValues({
            title: response?.data?.title || "",
            message: response?.data?.message || "",
            redirect_type: response?.data?.redirect_type || "",
            redirect_id: response?.data?.redirect_id || "",
          });
        }
      })
      .catch((error) => {
        console.log("notificationDetailsError ====> ", error);
      })
      .finally(() => {
        globalLoader(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRedirectTypeChange = (e) => {
    const val = e.target.value;
    formik.setFieldValue("redirect_type", val);
    formik.setFieldValue("redirect_id", "");
  };

  const handleRedirectItemChange = (e) => {
    const itemId = e.target.value;
    formik.setFieldValue("redirect_id", itemId);
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <h6 className="main-content-label mb-3">Edit Notification</h6>
            <form onSubmit={formik.handleSubmit}>
              <div className="row row-sm">
                <div className="col-lg-12 form-group">
                  <label htmlFor="title" className="text-left d-flex">
                    Title :<span className="requirestar">*</span>
                  </label>
                  <input
                    name="title"
                    id="title"
                    type="text"
                    className="form-control"
                    placeholder="Enter notification title"
                    maxLength={INPUT_LENGTH_100}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.title}
                  />
                  {formik.errors.title && formik.touched.title && (
                    <span className="text-danger d-flex text-left">
                      {formik.errors.title}
                    </span>
                  )}
                </div>

                <div className="col-lg-12 form-group">
                  <label htmlFor="message" className="text-left d-flex">
                    Message :<span className="requirestar">*</span>
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    className="form-control"
                    rows="5"
                    placeholder="Enter notification message"
                    maxLength={TEXTAREA_MAX_LENGTH_200}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.message}
                  />
                  {formik.errors.message && formik.touched.message && (
                    <span className="text-danger d-flex text-left">
                      {formik.errors.message}
                    </span>
                  )}
                </div>

                {/* Redirect Type Dropdown (Optional) */}
                <div className="col-lg-6 form-group">
                  <label htmlFor="redirect_type" className="text-left d-flex">
                    Redirect Type :
                  </label>
                  <select
                    name="redirect_type"
                    id="redirect_type"
                    onChange={handleRedirectTypeChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.redirect_type}
                    className="form-control"
                  >
                    <option value="">-- Select Redirect Type --</option>
                    <option value="puja">Puja</option>
                    <option value="report">Report</option>
                    <option value="astrologer">Astrologer</option>
                    <option value="product">Product</option>
                    <option value="free_service">Free Service</option>
                  </select>
                </div>

                {/* Dependent Redirect Item Dropdown (Optional) */}
                {formik.values.redirect_type && (
                  <div className="col-lg-6 form-group">
                    <label htmlFor="redirect_id" className="text-left d-flex">
                      Select {formik.values.redirect_type === "free_service" ? "Free Service" : formik.values.redirect_type.charAt(0).toUpperCase() + formik.values.redirect_type.slice(1)} :
                    </label>
                    <select
                      name="redirect_id"
                      id="redirect_id"
                      onChange={handleRedirectItemChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.redirect_id}
                      className="form-control"
                    >
                      <option value="">-- Select {formik.values.redirect_type === "free_service" ? "Free Service" : formik.values.redirect_type.charAt(0).toUpperCase() + formik.values.redirect_type.slice(1)} --</option>
                      {formik.values.redirect_type === "puja" && pujas.filter(item => item && (item.title || item.name)).map((item) => (
                        <option key={item.id} value={item.id}>{(item.title || item.name).trim()}</option>
                      ))}
                      {formik.values.redirect_type === "report" && reports.filter(item => item && (item.title || item.name)).map((item) => (
                        <option key={item.id} value={item.id}>{(item.title || item.name).trim()}</option>
                      ))}
                      {formik.values.redirect_type === "astrologer" && astrologers.filter(item => item && item.name && item.name.trim() !== "").map((item) => (
                        <option key={item.id} value={item.id}>{item.name.trim()}</option>
                      ))}
                      {formik.values.redirect_type === "product" && products.filter(item => item && (item.name || item.title)).map((item) => (
                        <option key={item.id} value={item.id}>{(item.name || item.title).trim()}</option>
                      ))}
                      {formik.values.redirect_type === "free_service" && freeServices.filter(item => item && (item.title || item.name)).map((item) => (
                        <option key={item.id} value={item.id}>{(item.title || item.name).trim()}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="col-md-12">
                  <button
                    className="btn btn-main-primary signbtn mr-2"
                    type="submit"
                    disabled={submitted}
                  >
                    <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                    {t("btn_submit")}
                  </button>
                  <button
                    className="btn ripple btn-secondary"
                    type="button"
                    onClick={() => navigate("/admin/notification")}
                    disabled={submitted}
                  >
                    <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                    {t("btn_cancel")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditNotification;
